'use client';

import type { ChangeEvent, HTMLInputTypeAttribute } from 'react';
import type {
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormReturn,
} from 'react-hook-form';
import { cn } from '@/utils/utils';
import { ChipsGroup } from '../ui/chips';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { PillsInput } from '../ui/pills-input';
import { Textarea } from '../ui/textarea';
import { SelectInput, type SelectOption } from './select-input';
import { sizes } from './styles';

interface BaseFormInputProps<TFieldValues extends FieldValues> {
  id: Path<TFieldValues>;
  label: string;
  description?: string;
  required?: boolean;
  size?: keyof typeof sizes;
  classesProps?: {
    containerClassName?: string;
    labelClassName?: string;
    errorClassName?: string;
  };
  form: UseFormReturn<TFieldValues>;
  middleware?: (value: string | number) => string | number;
  dataMiddleware?: (value: string | number) => string | number;
}

interface TextInputProps<TFieldValues extends FieldValues>
  extends BaseFormInputProps<TFieldValues> {
  type: Exclude<HTMLInputTypeAttribute, 'select' | 'textarea' | 'chips' | 'pills'>;
  placeholder?: string;
}

interface TextareaInputProps<TFieldValues extends FieldValues>
  extends BaseFormInputProps<TFieldValues> {
  type: 'textarea';
  placeholder?: string;
  rows?: number;
}

interface SelectInputFormProps<TFieldValues extends FieldValues>
  extends BaseFormInputProps<TFieldValues> {
  type: 'select';
  options?: SelectOption[];
}

interface ChipsInputProps<TFieldValues extends FieldValues>
  extends BaseFormInputProps<TFieldValues> {
  type: 'chips';
  options?: Array<{ value: string; label: string }>;
  multiSelect?: boolean;
}

interface PillsInputProps<TFieldValues extends FieldValues>
  extends BaseFormInputProps<TFieldValues> {
  type: 'pills';
  placeholder?: string;
}

export type FormInputProps<TFieldValues extends FieldValues = FieldValues> =
  | TextInputProps<TFieldValues>
  | TextareaInputProps<TFieldValues>
  | SelectInputFormProps<TFieldValues>
  | ChipsInputProps<TFieldValues>
  | PillsInputProps<TFieldValues>;

// Helper functions
const parseNumber = (value: string | number): string | number => {
  const parsed = Number.parseInt(value as string, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Function to format date as yyyy-MM-dd for HTML date input
const formatDateForInput = (dateValue: unknown): string => {
  if (!dateValue) {
    return '';
  }
  try {
    const date = new Date(String(dateValue));
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

// Convert between HTML date input format and Date objects
const parseDate = (value: string | number): Date => {
  try {
    // Parse the date from string or number
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) {
      return new Date();
    }

    // Return the date object
    return date;
  } catch {
    return new Date();
  }
};

export default function FormInput<TFieldValues extends FieldValues = FieldValues>(
  props: FormInputProps<TFieldValues>,
) {
  const {
    id,
    label,
    type,
    description,
    required = false,
    size = 'lg',
    classesProps,
    form,
    middleware,
    dataMiddleware,
  } = props;

  // Determine if we need automatic middleware based on input type
  const getEffectiveDataMiddleware = () => {
    if (dataMiddleware) {
      return dataMiddleware;
    }

    // Apply auto middleware based on type
    if (type === 'number') {
      return parseNumber;
    }
    if (type === 'date') {
      return parseDate;
    }

    return undefined;
  };

  const effectiveDataMiddleware = getEffectiveDataMiddleware();

  const renderFormControl = (
    field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>,
  ) => {
    // Create a modified field object that applies the middleware
    const modifiedField = {
      ...field,
      onChange: (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string | number,
      ) => {
        // For events (from Input and Textarea)
        if (e && typeof e === 'object' && 'target' in e) {
          const value = e.target.value;
          // Apply middleware if it exists
          if (middleware) {
            const transformedValue = middleware(value);
            field.onChange(transformedValue);
          } else if (effectiveDataMiddleware) {
            // If we have a dataMiddleware, we store the transformed value in the form
            // but we pass the original event to the input
            const transformedValue = effectiveDataMiddleware(value);
            field.onChange(transformedValue);
          } else {
            field.onChange(e);
          }
        } else {
          // For direct values (from SelectInput)
          if (middleware) {
            const transformedValue = middleware(e as string | number);
            field.onChange(transformedValue);
          } else if (effectiveDataMiddleware) {
            // Store the transformed value in the form but return the original value for display
            const transformedValue = effectiveDataMiddleware(e as string | number);
            field.onChange(transformedValue);
          } else {
            field.onChange(e);
          }
        }
      },
      // Format date values for HTML date inputs
      value: (() => {
        // Special case for dates: format them for display in date inputs
        if (type === 'date' && field.value) {
          // If field.value is a Date object, format it for the input
          if (Object.prototype.toString.call(field.value) === '[object Date]') {
            return formatDateForInput(field.value);
          }

          // If it's a string that looks like a date, format it
          if (
            typeof field.value === 'string' &&
            (field.value.includes('T') || field.value.includes('-'))
          ) {
            return formatDateForInput(field.value);
          }

          // Return as-is if it's already a valid format
          return field.value;
        }

        // Handle middleware transformed values
        if (effectiveDataMiddleware && field.value !== undefined) {
          // Convert numbers to strings for display
          if (typeof field.value === 'number') {
            return String(field.value);
          }
          return field.value;
        }

        // Default case
        return field.value;
      })(),
    };

    switch (type) {
      case 'textarea': {
        const textareaProps = props as TextareaInputProps<TFieldValues>;
        return (
          <Textarea
            id={id}
            {...modifiedField}
            placeholder={textareaProps.placeholder}
            rows={textareaProps.rows}
            className={cn(sizes[size])}
          />
        );
      }
      case 'select': {
        const selectProps = props as SelectInputFormProps<TFieldValues>;
        return (
          <SelectInput
            {...modifiedField}
            options={selectProps.options ?? []}
            className={cn(sizes[size])}
          />
        );
      }
      case 'chips': {
        const chipsProps = props as ChipsInputProps<TFieldValues>;
        return (
          <ChipsGroup
            name={id}
            control={form.control}
            options={chipsProps.options ?? []}
            multiSelect={chipsProps.multiSelect}
          />
        );
      }
      case 'pills': {
        const pillsProps = props as PillsInputProps<TFieldValues>;
        return (
          <PillsInput
            name={id}
            control={form.control}
            placeholder={pillsProps.placeholder}
            required={required}
          />
        );
      }
      default: {
        const inputProps = props as TextInputProps<TFieldValues>;
        return (
          <Input
            id={id}
            type={type}
            {...modifiedField}
            placeholder={inputProps.placeholder}
            className={cn(sizes[size])}
          />
        );
      }
    }
  };

  return (
    <FormField
      control={form.control}
      name={id}
      render={({ field }) => (
        <FormItem className={cn(classesProps?.containerClassName)}>
          <FormLabel className={cn(classesProps?.labelClassName)}>
            {label} {required && <span className="text-red-500">*</span>}
          </FormLabel>
          <FormControl>{renderFormControl(field)}</FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage className={cn('text-red-500', classesProps?.errorClassName)} />
        </FormItem>
      )}
    />
  );
}
